import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type {
  FetchSalesPointListParams,
  SalesPoint
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC
} from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import InfoIcon from '@mui/icons-material/Info'
import {
  Chip,
  type ChipProps,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  Tooltip
} from '@mui/material'
import { t } from 'ttag'

interface SalesPointListProps {
  rows: SalesPoint[]
  isLoading: boolean
  params: FetchSalesPointListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string) => void
  selectedSalesPointId: string | undefined
}

export function SalesPointList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedSalesPointId
}: Readonly<SalesPointListProps>) {
  const headers: HeadCell[] = [
    {
      id: 'salesPointId',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Sales point name`,
      sortable: true
    },
    ...(IS_WALTTI_SERVICE_ENVIRONMENT
      ? [
          {
            id: 'pta',
            numeric: false,
            disablePadding: false,
            label: t`Authority`,
            sortable: true
          }
        ]
      : []),
    {
      id: 'active',
      numeric: false,
      disablePadding: false,
      label: t`Status`,
      sortable: true
    },
    {
      id: 'salesAllowed',
      numeric: false,
      disablePadding: false,
      label: t`Sales allowed`,
      sortable: true
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  const chipPropsMapping: Record<string, Pick<ChipProps, 'color' | 'label'>> = {
    ACTIVE: { color: 'success', label: t`Active` },
    PASSIVE: { color: 'default', label: t`Passive` },
    YES: { color: 'success', label: t`Yes` },
    NO: { color: 'error', label: t`No` }
  }

  function getChipPropsByActivity(status: boolean) {
    return chipPropsMapping[status ? 'ACTIVE' : 'PASSIVE']
  }

  function getChipPropsByYesNo(field: boolean) {
    return chipPropsMapping[field ? 'YES' : 'NO']
  }

  function salesPointSalesAllowedMapping(field: boolean) {
    return field ? 'YES' : 'NO'
  }

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.sales_point_id}
                    selected={selectedSalesPointId === row.sales_point_id}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.sales_point_id)}>
                      {showValueOrHyphen(row.sales_point_id)}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.sales_point_id)}>
                      {showValueOrHyphen(row.name)}
                    </ClickableTableCell>
                    {IS_WALTTI_SERVICE_ENVIRONMENT && (
                      <NoWrapTableCell title={row.pta}>{row.pta}</NoWrapTableCell>
                    )}
                    <NoWrapTableCell title={row.active ? t`Active` : t`Passive`}>
                      <Chip size="small" {...getChipPropsByActivity(row.active)} />
                      {row.activity_end_date && row.active && (
                        <Tooltip
                          title={t`Activity ends` + ' ' + datetimeFormatter(row.activity_end_date)}
                          placement="top"
                        >
                          <IconButton sx={{ p: 0, ml: 1 }}>
                            <InfoIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={salesPointSalesAllowedMapping(row.sales_allowed)}>
                      <Chip size="small" {...getChipPropsByYesNo(row.sales_allowed)} />
                    </NoWrapTableCell>
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
